import React, { useState, useEffect, useCallback } from 'react';
// import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, IconButton, TextField, InputAdornment,
  Chip, Button, CircularProgress, Pagination, Tooltip, Alert,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
// import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StorageIcon from '@mui/icons-material/Storage';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ChatIcon from '@mui/icons-material/Chat';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ForumIcon from '@mui/icons-material/Forum';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EventIcon from '@mui/icons-material/Event';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { StyledDialog, inputStyles } from './common';

const API_BASE = 'https://turismo.nara.chat/api';

const TAB_CONFIG = [
  { key: 'usuarios', label: 'Usuarios', color: '#3b82f6', icon: <PersonIcon /> },
  { key: 'empresas', label: 'Empresas', color: '#10b981', icon: <BusinessIcon /> },
  { key: 'chatbots', label: 'Chatbots', color: '#8b5cf6', icon: <SmartToyIcon /> },
  { key: 'sesiones', label: 'Sesiones', color: '#f59e0b', icon: <ChatIcon /> },
  { key: 'memorias', label: 'Memorias', color: '#ec4899', icon: <PsychologyIcon /> },
  { key: 'conversaciones', label: 'Conversaciones', color: '#06b6d4', icon: <ForumIcon /> },
  { key: 'notificaciones', label: 'Notificaciones', color: '#f97316', icon: <NotificationsIcon /> },
  { key: 'citas', label: 'Citas', color: '#0ea5e9', icon: <EventIcon /> },
];

const ITEMS_PER_PAGE = 25;

export default function AdminDB() {
  const userId = localStorage.getItem('user_id');

  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Dialogs
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [snackMsg, setSnackMsg] = useState(null);

  // Filtros específicos de memorias
  const [filterChatbot, setFilterChatbot] = useState('');
  const [filterMemoryType, setFilterMemoryType] = useState('');
  const [chatbotsList, setChatbotsList] = useState([]);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/db/stats?user_id=${userId}`);
      if (res.ok) setStats(await res.json());
    } catch {}
  }, [userId]);

  const fetchChatbots = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/db/chatbots?user_id=${userId}&limit=100`);
      if (res.ok) {
        const data = await res.json();
        setChatbotsList((data.items || []).map(c => ({ id: c.id, name: c.name })));
      }
    } catch {}
  }, [userId]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    const tabKey = TAB_CONFIG[tab].key;
    const offset = (page - 1) * ITEMS_PER_PAGE;
    let url = `${API_BASE}/admin/db/${tabKey}?user_id=${userId}&limit=${ITEMS_PER_PAGE}&offset=${offset}`;
    if (search) url += `&buscar=${encodeURIComponent(search)}`;
    if (tabKey === 'memorias') {
      if (filterChatbot) url += `&chatbot_id=${filterChatbot}`;
      if (filterMemoryType) url += `&memory_type=${encodeURIComponent(filterMemoryType)}`;
    }

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [tab, page, search, userId, filterChatbot, filterMemoryType]);

  useEffect(() => { fetchStats(); fetchChatbots(); }, [fetchStats, fetchChatbots]);
  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { setPage(1); setSearch(''); setFilterChatbot(''); setFilterMemoryType(''); }, [tab]);

  // Debounced search
  const [searchTimer, setSearchTimer] = useState(null);
  const handleSearch = (val) => {
    setSearch(val);
    if (searchTimer) clearTimeout(searchTimer);
    setSearchTimer(setTimeout(() => { setPage(1); }, 300));
  };

  // ============================================================================
  // DETAIL
  // ============================================================================

  const openDetail = async (item) => {
    const tabKey = TAB_CONFIG[tab].key;
    // sesiones uses 'id', others use 'id'
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailData(null);

    try {
      const res = await fetch(`${API_BASE}/admin/db/${tabKey}/${item.id}?user_id=${userId}`);
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setDetailData(await res.json());
    } catch (e) {
      setDetailData({ _error: e.message });
    } finally {
      setDetailLoading(false);
    }
  };

  // ============================================================================
  // EDIT
  // ============================================================================

  const canEdit = ['usuarios', 'empresas', 'chatbots', 'citas'].includes(TAB_CONFIG[tab].key);

  const openEdit = (item) => {
    const tabKey = TAB_CONFIG[tab].key;
    const fields = {};
    if (tabKey === 'usuarios') {
      fields.name = item.name || '';
      fields.lastname = item.lastname || '';
      fields.email = item.email || '';
      fields.role = item.role || '';
      fields.timezone = item.timezone || '';
      fields.whatsapp = item.whatsapp || '';
    } else if (tabKey === 'empresas') {
      fields.company = item.company || '';
      fields.address = item.address || '';
      fields.city = item.city || '';
      fields.country = item.country || '';
      fields.estado_suscripcion = item.estado_suscripcion || '';
    } else if (tabKey === 'chatbots') {
      fields.name = item.name || '';
      fields.description = item.description || '';
      fields.flow_type = item.flow_type || '';
    } else if (tabKey === 'citas') {
      fields.estado = item.estado || '';
      fields.notas = item.notas || '';
      fields.servicio = item.servicio || '';
    }
    setEditData({ id: item.id, ...fields });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    const tabKey = TAB_CONFIG[tab].key;
    setEditSaving(true);
    try {
      const { id, ...fields } = editData;
      const params = new URLSearchParams({ user_id: userId });
      Object.entries(fields).forEach(([k, v]) => {
        if (v !== null && v !== undefined) params.append(k, v);
      });
      const res = await fetch(`${API_BASE}/admin/db/${tabKey}/${id}?${params.toString()}`, { method: 'PUT' });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setEditOpen(false);
      setSnackMsg('Actualizado correctamente');
      fetchItems();
      fetchStats();
    } catch (e) {
      setSnackMsg(`Error: ${e.message}`);
    } finally {
      setEditSaving(false);
    }
  };

  // ============================================================================
  // DELETE
  // ============================================================================

  const openDelete = (item) => {
    setDeleteTarget(item);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    const tabKey = TAB_CONFIG[tab].key;
    setDeleteLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/db/${tabKey}/${deleteTarget.id}?user_id=${userId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setDeleteOpen(false);
      setSnackMsg('Eliminado correctamente');
      fetchItems();
      fetchStats();
    } catch (e) {
      setSnackMsg(`Error: ${e.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ============================================================================
  // CLEANUP
  // ============================================================================

  const canCleanup = ['sesiones', 'memorias', 'notificaciones'].includes(TAB_CONFIG[tab].key);

  const confirmCleanup = async () => {
    const tabKey = TAB_CONFIG[tab].key;
    setCleanupLoading(true);
    let url = '';
    if (tabKey === 'sesiones') url = `${API_BASE}/admin/db/sesiones/cleanup/old?user_id=${userId}&horas=24`;
    else if (tabKey === 'memorias') url = `${API_BASE}/admin/db/memorias/cleanup/expired?user_id=${userId}`;
    else if (tabKey === 'notificaciones') url = `${API_BASE}/admin/db/notificaciones/cleanup/old?user_id=${userId}&dias=30`;

    try {
      const res = await fetch(url, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setCleanupOpen(false);
      setSnackMsg(data.message);
      fetchItems();
      fetchStats();
    } catch (e) {
      setSnackMsg(`Error: ${e.message}`);
    } finally {
      setCleanupLoading(false);
    }
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const currentTab = TAB_CONFIG[tab];
  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const getItemTitle = (item) => {
    const k = currentTab.key;
    if (k === 'usuarios') return `${item.name} ${item.lastname}`;
    if (k === 'empresas') return item.company;
    if (k === 'chatbots') return item.name;
    if (k === 'sesiones') return item.numero_telefono;
    if (k === 'memorias') return item.memory_type;
    if (k === 'conversaciones') return item.nombre_contacto || item.numero_telefono;
    if (k === 'notificaciones') return item.titulo;
    if (k === 'citas') return item.paciente_nombre;
    return `#${item.id}`;
  };

  const getItemSubtitle = (item) => {
    const k = currentTab.key;
    if (k === 'usuarios') return item.email;
    if (k === 'empresas') return `${item.city} - ${item.estado_suscripcion}`;
    if (k === 'chatbots') return `${item.owner_name || 'Sin dueño'} - ${item.flow_type}`;
    if (k === 'sesiones') return item.chatbot_name || `Chatbot #${item.chatbot_id}`;
    if (k === 'memorias') return item.content;
    if (k === 'conversaciones') return item.resumen || 'Sin resumen';
    if (k === 'notificaciones') return item.mensaje;
    if (k === 'citas') return item.fecha_cita ? new Date(item.fecha_cita).toLocaleString() : '';
    return '';
  };

  const getItemChips = (item) => {
    const k = currentTab.key;
    const chips = [];
    if (k === 'usuarios') {
      chips.push({ label: item.role || 'admin', color: item.role === 'superadmin' ? '#dc2626' : '#3b82f6' });
      if (item.empresa_nombre) chips.push({ label: item.empresa_nombre, color: '#64748b' });
    } else if (k === 'empresas') {
      chips.push({ label: item.estado_suscripcion || 'activa', color: item.estado_suscripcion === 'activa' ? '#10b981' : '#f59e0b' });
      chips.push({ label: `${item.chatbots_activos || 0} chatbots`, color: '#8b5cf6' });
    } else if (k === 'chatbots') {
      chips.push({ label: item.flow_type || 'traditional', color: item.flow_type === 'intelligent' ? '#8b5cf6' : '#64748b' });
      if (item.whatsapp_number) chips.push({ label: 'WhatsApp', color: '#25D366' });
      chips.push({ label: `${item.conversaciones_totales} convs`, color: '#06b6d4' });
    } else if (k === 'sesiones') {
      if (item.escalacion_estado) chips.push({ label: item.escalacion_estado, color: '#dc2626' });
      chips.push({ label: `${item.historial_msgs} msgs`, color: '#64748b' });
      if (item.consentimiento_privacidad) chips.push({ label: 'Consent', color: '#10b981' });
    } else if (k === 'memorias') {
      chips.push({ label: item.memory_type, color: '#ec4899' });
      if (item.is_expired) chips.push({ label: 'Expirada', color: '#dc2626' });
      if (item.has_embedding) chips.push({ label: 'Embedding', color: '#8b5cf6' });
    } else if (k === 'conversaciones') {
      const resColor = { completada: '#10b981', escalada: '#dc2626', timeout: '#f59e0b', rechazada: '#64748b' };
      chips.push({ label: item.resultado || 'activa', color: resColor[item.resultado] || '#3b82f6' });
      chips.push({ label: `${item.total_mensajes} msgs`, color: '#64748b' });
    } else if (k === 'notificaciones') {
      chips.push({ label: item.tipo, color: '#f97316' });
      chips.push({ label: item.leida ? 'Leida' : 'No leida', color: item.leida ? '#64748b' : '#3b82f6' });
    } else if (k === 'citas') {
      const estColor = { confirmado: '#10b981', cancelado: '#dc2626' };
      chips.push({ label: item.estado || 'pendiente', color: estColor[item.estado] || '#f59e0b' });
      if (item.servicio) chips.push({ label: item.servicio, color: '#64748b' });
    }
    return chips;
  };

  const getItemMeta = (item) => {
    if (item.created_at) return new Date(item.created_at).toLocaleDateString();
    if (item.inicio) return new Date(item.inicio).toLocaleDateString();
    return '';
  };

  // ============================================================================
  // RENDER DETAIL
  // ============================================================================

  const renderDetailContent = () => {
    if (detailLoading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={32} /></Box>;
    if (!detailData) return null;
    if (detailData._error) return <Alert severity="error">{detailData._error}</Alert>;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {Object.entries(detailData).map(([key, value]) => {
          if (key === 'mensajes' && Array.isArray(value)) {
            return (
              <Box key={key}>
                <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>{key}</Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto', p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  {value.map((msg, i) => (
                    <Box key={i} sx={{ mb: 1, p: 1, borderRadius: 1, bgcolor: msg.role === 'assistant' ? '#eff6ff' : '#fff', border: '1px solid #e2e8f0' }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: msg.role === 'assistant' ? '#3b82f6' : '#64748b', mb: 0.25 }}>
                        {msg.role === 'assistant' ? 'NARA' : 'Paciente'}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#1e293b', whiteSpace: 'pre-wrap' }}>
                        {typeof msg.content === 'string' ? msg.content.substring(0, 500) : JSON.stringify(msg.content).substring(0, 500)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          }
          if (key === 'variables' && typeof value === 'object') {
            return (
              <Box key={key}>
                <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>{key}</Typography>
                <Box sx={{ maxHeight: 250, overflow: 'auto', p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <pre style={{ fontSize: 11, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(value, null, 2)}
                  </pre>
                </Box>
              </Box>
            );
          }
          if (key === 'chatbots' && Array.isArray(value)) {
            return (
              <Box key={key}>
                <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>{key}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {value.map((c, i) => (
                    <Chip key={i} label={`${c.name} (${c.flow_type})`} size="small" sx={{ fontSize: 11 }} />
                  ))}
                </Box>
              </Box>
            );
          }
          if (key === 'usuarios' && Array.isArray(value)) {
            return (
              <Box key={key}>
                <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>{key}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {value.map((u, i) => (
                    <Chip key={i} label={`${u.name} (${u.role})`} size="small" sx={{ fontSize: 11 }} />
                  ))}
                </Box>
              </Box>
            );
          }
          // Skip null, empty arrays, embeddings
          if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) return null;
          if (key === 'embedding') return null;

          const displayVal = typeof value === 'boolean' ? (value ? 'Si' : 'No')
            : typeof value === 'object' ? JSON.stringify(value)
            : String(value);

          return (
            <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 0.5, borderBottom: '1px solid #f1f5f9' }}>
              <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 600, minWidth: 140, flexShrink: 0 }}>{key}</Typography>
              <Typography sx={{ fontSize: 12, color: '#1e293b', textAlign: 'right', wordBreak: 'break-all', maxWidth: 300 }}>{displayVal}</Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  // ============================================================================
  // EDIT FORM
  // ============================================================================

  const renderEditFields = () => {
    const { id, ...fields } = editData;
    return Object.entries(fields).map(([key, value]) => (
      <TextField
        key={key}
        sx={inputStyles}
        label={key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
        value={value || ''}
        onChange={(e) => setEditData(prev => ({ ...prev, [key]: e.target.value }))}
        fullWidth
        size="small"
        multiline={key === 'description' || key === 'notas'}
        rows={key === 'description' || key === 'notas' ? 3 : 1}
      />
    ));
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9' }}>

      {/* HEADER */}
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, maxWidth: 1400, mx: 'auto' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#1e293b' }}>Base de Datos</Typography>
            <Typography sx={{ fontSize: 14, color: '#64748b' }}>Administracion de registros</Typography>
          </Box>
          <IconButton onClick={() => { fetchStats(); fetchItems(); }} sx={{ color: '#64748b', '&:hover': { color: '#1e3a5f' } }}>
            <RefreshIcon />
          </IconButton>
        </Box>

        {/* STATS CHIPS */}
        {stats && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, maxWidth: 1400, mx: 'auto' }}>
            {TAB_CONFIG.map((t) => (
              <Chip
                key={t.key}
                icon={React.cloneElement(t.icon, { sx: { fontSize: 16, color: tab === TAB_CONFIG.indexOf(t) ? '#fff !important' : `${t.color} !important` } })}
                label={`${stats[t.key] || 0} ${t.label}`}
                onClick={() => setTab(TAB_CONFIG.indexOf(t))}
                sx={{
                  bgcolor: tab === TAB_CONFIG.indexOf(t) ? t.color : '#e2e8f0',
                  color: tab === TAB_CONFIG.indexOf(t) ? '#fff' : '#475569',
                  fontWeight: 600, fontSize: 12,
                  '&:hover': { bgcolor: t.color, color: '#fff' },
                  transition: 'all 0.2s',
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ maxWidth: 1400, mx: 'auto', px: 2 }}>
        {/* SEARCH BAR + ACTIONS */}
        <Box sx={{ display: 'flex', gap: 1.5, mt: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={`Buscar en ${currentTab.label}...`}
            size="small"
            sx={{ flex: 1, minWidth: 200, ...inputStyles, '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 3 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} /></InputAdornment>,
            }}
          />
          {currentTab.key === 'memorias' && (
            <>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: 13 }}>Chatbot</InputLabel>
                <Select
                  value={filterChatbot}
                  label="Chatbot"
                  onChange={(e) => { setFilterChatbot(e.target.value); setPage(1); }}
                  sx={{ bgcolor: '#fff', borderRadius: 2, fontSize: 13 }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {chatbotsList.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: 13 }}>Tipo</InputLabel>
                <Select
                  value={filterMemoryType}
                  label="Tipo"
                  onChange={(e) => { setFilterMemoryType(e.target.value); setPage(1); }}
                  sx={{ bgcolor: '#fff', borderRadius: 2, fontSize: 13 }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {['conversacion', 'historial_medico', 'recomendacion', 'sintoma', 'alergia', 'medicamento', 'preferencia_doctor', 'preferencia_horario', 'consulta_precio'].map(t => (
                    <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          {canCleanup && (
            <Button
              variant="outlined"
              startIcon={<CleaningServicesIcon />}
              onClick={() => setCleanupOpen(true)}
              sx={{ borderColor: '#e2e8f0', color: '#64748b', textTransform: 'none', fontWeight: 600, fontSize: 12, borderRadius: 2, whiteSpace: 'nowrap' }}
            >
              Limpiar
            </Button>
          )}
          <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {total} registros
          </Typography>
        </Box>

        {/* CONTENT */}
        {loading ? (
          <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress size={40} /></Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : items.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center', background: '#fff', borderRadius: 4, border: '1px solid #e2e8f0' }}>
            {React.cloneElement(currentTab.icon, { sx: { fontSize: 56, color: '#cbd5e1', mb: 1.5 } })}
            <Typography sx={{ color: '#1e293b', fontSize: 16, fontWeight: 600 }}>Sin registros</Typography>
            <Typography sx={{ color: '#64748b', fontSize: 13 }}>No hay datos para mostrar</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {items.map((item) => {
                const chips = getItemChips(item);
                return (
                  <Box
                    key={item.id}
                    sx={{
                      p: 2, borderRadius: 3,
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                      cursor: 'pointer',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""', position: 'absolute', top: 0, left: 0, width: 3, height: '100%',
                        background: currentTab.color, borderRadius: '3px 0 0 3px',
                      },
                      '&:hover': {
                        transform: 'translateX(2px)',
                        boxShadow: `0 4px 20px rgba(0,0,0,0.08), 0 0 0 1px ${currentTab.color}20`,
                      },
                    }}
                    onClick={() => openDetail(item)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {/* Icon */}
                      <Box sx={{
                        width: 40, height: 40, borderRadius: 2.5,
                        background: `linear-gradient(135deg, ${currentTab.color}15 0%, ${currentTab.color}08 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: currentTab.color }}>
                          #{item.id}
                        </Typography>
                      </Box>

                      {/* Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {getItemTitle(item)}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {getItemSubtitle(item)}
                        </Typography>
                      </Box>

                      {/* Chips */}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: 300 }}>
                        {chips.map((c, i) => (
                          <Chip
                            key={i}
                            label={c.label}
                            size="small"
                            sx={{
                              height: 22, fontSize: 10, fontWeight: 700,
                              bgcolor: `${c.color}15`, color: c.color, border: `1px solid ${c.color}30`,
                            }}
                          />
                        ))}
                      </Box>

                      {/* Meta */}
                      <Typography sx={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap', ml: 1 }}>
                        {getItemMeta(item)}
                      </Typography>

                      {/* Actions */}
                      <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Ver detalle">
                          <IconButton size="small" onClick={() => openDetail(item)}>
                            <VisibilityIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                          </IconButton>
                        </Tooltip>
                        {canEdit && (
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => openEdit(item)}>
                              <EditIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => openDelete(item)}>
                            <DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* PAGINATION */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pb: 4 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  color="primary"
                  sx={{ '& .Mui-selected': { bgcolor: `${currentTab.color} !important` } }}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* ============================================================================ */}
      {/* DETAIL DIALOG */}
      {/* ============================================================================ */}
      <StyledDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title={detailData ? getItemTitle(detailData) || `Detalle #${detailData.id}` : 'Cargando...'}
        icon={currentTab.icon}
        variant="compact"
        maxWidth="sm"
      >
        <StyledDialog.Body compact>
          {renderDetailContent()}
        </StyledDialog.Body>
        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => setDetailOpen(false)}>
            Cerrar
          </StyledDialog.CancelButton>
        </StyledDialog.CompactActions>
      </StyledDialog>

      {/* ============================================================================ */}
      {/* EDIT DIALOG */}
      {/* ============================================================================ */}
      <StyledDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={`Editar ${currentTab.label.slice(0, -1)}`}
        icon={<EditIcon />}
        variant="compact"
        maxWidth="xs"
      >
        <StyledDialog.Body compact>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {renderEditFields()}
          </Box>
        </StyledDialog.Body>
        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => setEditOpen(false)} />
          <StyledDialog.PrimaryButton fullWidth onClick={saveEdit} loading={editSaving}>
            Guardar
          </StyledDialog.PrimaryButton>
        </StyledDialog.CompactActions>
      </StyledDialog>

      {/* ============================================================================ */}
      {/* DELETE DIALOG */}
      {/* ============================================================================ */}
      <StyledDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Confirmar eliminacion"
        icon={<WarningAmberIcon />}
        variant="compact"
        maxWidth="xs"
      >
        <StyledDialog.Body compact>
          <Alert severity="warning" sx={{ mb: 1 }}>
            Esta accion no se puede deshacer.
          </Alert>
          <Typography sx={{ fontSize: 13, color: '#475569' }}>
            Eliminar <strong>{deleteTarget ? getItemTitle(deleteTarget) : ''}</strong> (#{deleteTarget?.id})?
          </Typography>
        </StyledDialog.Body>
        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => setDeleteOpen(false)} />
          <Button
            fullWidth
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteLoading}
            sx={{
              bgcolor: '#dc2626', color: '#fff', textTransform: 'none', fontWeight: 700,
              borderRadius: 2, '&:hover': { bgcolor: '#b91c1c' },
            }}
          >
            {deleteLoading ? <CircularProgress size={20} color="inherit" /> : 'Eliminar'}
          </Button>
        </StyledDialog.CompactActions>
      </StyledDialog>

      {/* ============================================================================ */}
      {/* CLEANUP DIALOG */}
      {/* ============================================================================ */}
      <StyledDialog
        open={cleanupOpen}
        onClose={() => setCleanupOpen(false)}
        title={`Limpiar ${currentTab.label}`}
        icon={<CleaningServicesIcon />}
        variant="compact"
        maxWidth="xs"
      >
        <StyledDialog.Body compact>
          <Alert severity="info" sx={{ mb: 1 }}>
            {currentTab.key === 'sesiones' && 'Eliminara sesiones inactivas por mas de 24 horas.'}
            {currentTab.key === 'memorias' && 'Eliminara memorias semanticas expiradas.'}
            {currentTab.key === 'notificaciones' && 'Eliminara notificaciones leidas con mas de 30 dias.'}
          </Alert>
        </StyledDialog.Body>
        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => setCleanupOpen(false)} />
          <StyledDialog.PrimaryButton fullWidth onClick={confirmCleanup} loading={cleanupLoading}>
            Limpiar
          </StyledDialog.PrimaryButton>
        </StyledDialog.CompactActions>
      </StyledDialog>

      {/* SNACKBAR */}
      {snackMsg && (
        <Alert
          severity={snackMsg.startsWith('Error') ? 'error' : 'success'}
          onClose={() => setSnackMsg(null)}
          sx={{
            position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            zIndex: 9999, minWidth: 300, boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          }}
        >
          {snackMsg}
        </Alert>
      )}
    </Box>
  );
}
